import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import Add from "../../../../icons/add.svg";
import Delete from "../../../../icons/delete.svg";
import { Pencil } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { UsuarioContext } from "../../../../providers/userContext";
import BreadCrumb from "../../../../components/navbar/Breadcrumb";
import { Box, Tooltip } from "@mui/material";
import { DataTable } from "../../../../components";
import { construirURLParamsFiltros, useRecogerURLParamsFiltros } from "../../../../functions/parametrosURL/funcionesParamsURL";
import { withRouter } from "../../../../providers/withRouter";
import { getProductosKae, deleteProductoKae } from "../../../../services";

const Productos = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { filtroOrder } = useRecogerURLParamsFiltros();
  const user = useContext(UsuarioContext);
  const [loading, setLoading] = useState(true);
  const [recargar, setRecargar] = useState(true);
  const [data, setData] = useState({
    meta: {},
    rows: [],
  });

  const fetchData = async ({ offset, limit, filtros = {}, order, orderBy } = {}) => {
    orderBy = orderBy || filtroOrder.ordenadoPor || 'id';
    order = order || filtroOrder.orden || 'ASC';

    construirURLParamsFiltros(navigate, offset || 1, limit || 50, filtros, order, orderBy);

    try {
      setLoading(true);
      const response = await getProductosKae();
      const items = Array.isArray(response) ? response : [];
      setData({
        meta: {
          total: items.length,
          per_page: items.length,
          last_page: 1,
          has_more_pages: false,
        },
        rows: formatData(items),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const formatData = (data) => {
    return data.map((row) => ({
      id: row.id,
      nombre: row.nombre,
      sku: row.sku,
      categoria: row.categoria?.nombre || "",
      descripcion: row.descripcion,
      obligatorio: row.obligatorio,
      precio_compra: row.precio_compra,
      precio_venta: row.precio_venta,
    }));
  };

  const CellTooltip = ({ value }) => (
    <Tooltip title={value || ""} arrow>
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: 200 }}>
        {value || ""}
      </span>
    </Tooltip>
  );

  const productosHeaders = [
    {
      id: "nombre",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.name'),
      overrideFunc: (data, row) => <CellTooltip value={row.nombre} />,
    },
    {
      id: "sku",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.sku'),
      overrideFunc: (data, row) => <CellTooltip value={row.sku} />,
    },
    {
      id: "categoria",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.category'),
      noObjeto: true,
      overrideFunc: (data, row) => <CellTooltip value={row.categoria} />,
    },
    {
      id: "descripcion",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.description'),
      overrideFunc: (data, row) => (
        <Tooltip title={row.descripcion || ""} arrow>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: 150 }}>
            {row.descripcion || ""}
          </span>
        </Tooltip>
      ),
    },
    {
      id: "obligatorio",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.mandatory'),
      overrideFunc: (data) => (
        <span>{data ? t('status.yes') : t('status.no')}</span>
      ),
    },
    {
      id: "precio_compra",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.purchasePrice'),
      overrideFunc: (data, row) => {
        const val = row.precio_compra ? `${row.precio_compra} €` : "";
        return <CellTooltip value={val} />;
      },
    },
    {
      id: "precio_venta",
      numeric: false,
      disablePadding: false,
      label: t('products.columns.salePrice'),
      overrideFunc: (data, row) => {
        const val = row.precio_venta ? `${row.precio_venta} €` : "";
        return <CellTooltip value={val} />;
      },
    },
    {
      id: "acciones",
      numeric: false,
      disablePadding: false,
      label: "",
      noObjeto: true,
      overrideFunc: (data, row) => (
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          {(user.rol === "ADMIN" || user.rol === "VENT" || user.rol === "TECH" || user.rol === "TECH_COOR") && (
            <Link to={`/productos/editar/${row.id}`}>
              <Pencil size={20} className="tableIcon" />
            </Link>
          )}
          {(user.rol === "ADMIN" || user.rol === "TECH" || user.rol === "TECH_COOR") && (
            <img
              src={Delete}
              alt={t('actions.delete')}
              className="tableIcon"
              onClick={() => eliminarProducto(row)}
            />
          )}
        </div>
      ),
    },
  ];

  const eliminarProducto = (prod) => {
    Swal.fire({
      title: t('products.messages.confirmDelete'),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      cancelButtonText: t('actions.cancel'),
      confirmButtonText: t('messages.yesDelete'),
    })
      .then((result) => {
        if (result.isConfirmed) {
          deleteProductoKae(prod.id)
            .then((res) => {
              if (res.message === "PRODUCTO_KAE_ELIMINADO") {
                toast.success(t('products.messages.deleted'));
                setRecargar(!recargar);
              } else {
                toast.error(t('products.messages.deleteError'));
              }
            })
            .catch((e) => {
              toast.error(t('products.messages.deleteError'));
              console.log(e);
            });
        }
      })
      .catch((e) => {
        toast.error(t('messages.error'));
        console.log(e);
      });
  };

  return (
    <>
      <BreadCrumb rutas={[{ path: "/productos", label: t('products.title') }]} />
      <div className="listPage">
        <div className="gridCard">
          <div className="gridHead">
            {t('products.title').toUpperCase()}
          </div>
        </div>
      </div>
      <div className="pageContent tableContainer">
        <div className="contentBar">
          <div style={{ display: "flex" }}></div>
          {(user.rol === "ADMIN" || user.rol === "VENT" || user.rol === "TECH" || user.rol === "TECH_COOR") && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "end" }}>
              <Link to="/productos/nuevo">
                <div className="contentButton mx-2">
                  <img src={Add} alt={t('products.new')} />
                </div>
              </Link>
            </div>
          )}
        </div>
        <div className="tableWrapper">
          <Box className="pageContent">
            <Box sx={{ mb: 10 }}>
              <DataTable
                data={data.rows}
                isLoading={loading}
                headers={productosHeaders}
                fetcher={fetchData}
                currentPage={data.current_page}
                meta={data.meta}
                totalItems={data.total}
                totalPages={data.totalPages}
                nextPage={data.next_page}
                mensaje={t('products.messages.noProducts')}
                recargar={recargar}
                clicable={(user.rol === "ADMIN" || user.rol === "VENT" || user.rol === "TECH" || user.rol === "TECH_COOR")}
                onClickRow={(row) => (user.rol === "ADMIN" || user.rol === "VENT" || user.rol === "TECH" || user.rol === "TECH_COOR") && navigate(`/productos/editar/${row.id}`)}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
};

export default withRouter(Productos);
